// src/pages/Kanban.jsx

// src/pages/Kanban.jsx

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { kanbanAPI, aoAPI, corbeilleAPI, candidateAPI, cvAPI, resourcesAPI, candidatePositioningAPI, companiesAPI } from '../services/api';
import { initials } from '../utils/helpers';
import { useAuth, extractError } from '../context/AuthContext';
import {
  Search, Plus, RefreshCw, MapPin, Building2,
  Users, Star, Award, ChevronRight, Trash2, UserCheck,
  Loader, ChevronDown, CheckCircle, Trophy, XCircle, X,
  RotateCcw, Clock3, ChevronUp, ArrowRight,
  RotateCcw as Restore, AlertTriangle,
  Eye, Calendar, Play, Target, Layers,
  LayoutGrid, List,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AOModal from '../components/ao/AOModal';
import { AssignIAModal, SendToTAModal } from '../components/ao/WorkflowModals';
import api from '../services/api';
import './Kanban.css';

const STATUS_CONFIG = {
  NOUVEAU:                            { label: 'Nouveau',            kanbanColor: '#FF4100' },
  EN_COURS_DE_TRAITEMENT:             { label: 'En traitement',      kanbanColor: '#f05a1a' },
  ENVOYE_A_EQUIPE_TALENT_ACQUISITION: { label: 'Envoyé TA',          kanbanColor: '#0891b2' },
  EN_COURS_DE_SOURCING:               { label: 'En sourcing',        kanbanColor: '#d97706' },
  SHORT_LIST_ENVOYEE:                 { label: 'Shortlist',          kanbanColor: '#6366f1' },
  TOP_PROFIL_ENVOYE_AU_CLIENT:        { label: 'On a le top profil', kanbanColor: '#059669' },
  REPONDU:                            { label: 'Répondu',            kanbanColor: '#16a34a' },
  GAGNE:                              { label: 'Gagné 🏆',           kanbanColor: '#4ADE80' },
  PERDU:                              { label: 'Perdu',              kanbanColor: '#dc2626' },
  TERMINE:                            { label: 'Clôturé',            kanbanColor: 'var(--text-primary)' },
  ABANDONNE:                          { label: 'Abandonné',          kanbanColor: '#78350f' },
  SUPPRIME:                           { label: '🗑 Corbeille',       kanbanColor: 'var(--text-muted)' },
};

const STATUS_META = {
  NOUVEAU:                            { label: 'Nouveau',           color: '#FF4100', bg: 'var(--orange-50)' },
  EN_COURS_DE_TRAITEMENT:             { label: 'En cours',          color: '#F59E0B', bg: '#FFFBEB' },
  ENVOYE_A_EQUIPE_TALENT_ACQUISITION: { label: 'Envoyé TA',         color: '#A78BFA', bg: 'rgba(139,92,246,0.10)' },
  EN_COURS_DE_SOURCING:               { label: 'En sourcing',       color: '#06B6D4', bg: 'rgba(8,145,178,0.10)' },
  SHORT_LIST_ENVOYEE:                 { label: 'Shortlist envoyée', color: '#10B981', bg: '#ECFDF5' },
  TOP_PROFIL_ENVOYE_AU_CLIENT:        { label: 'Top profil envoyé', color: '#22C55E', bg: 'rgba(34,197,94,0.08)' },
  REPONDU:                            { label: 'Répondu',           color: '#0EA5E9', bg: '#F0F9FF' },
  GAGNE:                              { label: 'Gagné',             color: '#16A34A', bg: 'rgba(34,197,94,0.08)' },
  PERDU:                              { label: 'Perdu',             color: '#DC2626', bg: 'rgba(239,68,68,0.12)' },
  TERMINE:                            { label: 'Clôturé',           color: 'var(--text-primary)', bg: 'rgba(255,255,255,0.06)' },
  ABANDONNE:                          { label: 'Abandonné',         color: '#78350f', bg: '#FEF3C7' },
};

const COLUMNS = [
  'NOUVEAU', 'EN_COURS_DE_TRAITEMENT', 'ENVOYE_A_EQUIPE_TALENT_ACQUISITION',
  'EN_COURS_DE_SOURCING', 'SHORT_LIST_ENVOYEE', 'TOP_PROFIL_ENVOYE_AU_CLIENT',
  'REPONDU', 'GAGNE', 'PERDU', 'TERMINE', 'ABANDONNE', 'SUPPRIME',
];

const EARLY_PIPELINE_STATUSES = new Set([
  'NOUVEAU',
  'EN_COURS_DE_TRAITEMENT',
  'ENVOYE_A_EQUIPE_TALENT_ACQUISITION',
]);

const normalizeStatus = (s) => (s || '').toUpperCase();
const shouldHidePipelineStats = (status) => EARLY_PIPELINE_STATUSES.has(normalizeStatus(status));

const formatDuration = (seconds) => {
  if (seconds == null) return '-';
  const total = Number(seconds);
  if (Number.isNaN(total) || total < 0) return '-';
  const days    = Math.floor(total / 86400);
  const hours   = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const parts = [];
  if (days > 0)    parts.push(`${days}j`);
  if (hours > 0)   parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}min`);
  return parts.length ? parts.join(' ') : '0min';
};

// ─────────────────────────────────────────────────────────────
// Routeur principal
// ─────────────────────────────────────────────────────────────
export default function Kanban() {
  const { user } = useAuth();
  const role = (user?.role || '').toUpperCase();
  if (role === 'TALENT_ACQUISITION') return <TAMissions />;
  return <KanbanBoard />;
}

// ─────────────────────────────────────────────────────────────
// Vue TA : AOs en cours de sourcing + AOs clôturés
// ─────────────────────────────────────────────────────────────
const TA_MISSIONS_CLOSED = new Set([
  'SHORT_LIST_ENVOYEE', 'TOP_PROFIL_ENVOYE_AU_CLIENT',
  'REPONDU', 'GAGNE', 'PERDU', 'ANNULE', 'TERMINE', 'SUPPRIME',
]);

function TAMissions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myAOs,   setMyAOs]   = useState([]);
  const [search,  setSearch]  = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/ao-management/mine');
      setMyAOs(res.data || []);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData]);

  const q = search.toLowerCase();
  const matches = (ao) =>
    !search ||
    (ao.job_title || '').toLowerCase().includes(q) ||
    (ao.client || '').toLowerCase().includes(q);

  const activeMissions = myAOs.filter(ao =>
    (ao.ao_avancement || '').toUpperCase() === 'EN_COURS_DE_SOURCING' && matches(ao)
  );
  const closedMissions = myAOs.filter(ao =>
    TA_MISSIONS_CLOSED.has((ao.ao_avancement || '').toUpperCase()) && matches(ao)
  );

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', gap:12, color:'var(--text-muted)' }}>
      <div className="spinner"/> Chargement…
    </div>
  );

  const MissionRow = ({ ao }) => {
    const meta = STATUS_META[(ao.ao_avancement || '').toUpperCase()] || { label: ao.ao_avancement, color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
    return (
      <tr key={ao.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/ao/${ao.id}`)}>
        <td>
          <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{ao.job_title || 'Sans titre'}</div>
          <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{ao.source_type}</div>
        </td>
        <td style={{ fontSize:'0.8125rem' }}>{ao.client || '—'}</td>
        <td>
          <span style={{ padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:meta.bg, color:meta.color }}>
            {meta.label}
          </span>
        </td>
        <td style={{ fontSize:'0.8125rem', fontWeight:600, color:'#EA580C' }}>{ao.tjm || '—'}</td>
        <td>
          {ao.response_deadline
            ? <DeadlineBadge deadline={ao.response_deadline}/>
            : <span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>—</span>}
        </td>
        <td style={{ fontSize:'0.875rem', fontWeight:600, color:'#FF4100' }}>
          {ao.scored_candidates_count || 0}
        </td>
        <td onClick={e => e.stopPropagation()}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/ao/${ao.id}`)}>
            <Eye size={13}/> Voir
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Mes missions</h1>
          <p>Bonjour {user?.prenom} — vos appels d'offres à sourcer</p>
        </div>
        <div className="page-header-actions">
          <div style={{ position:'relative' }}>
            <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }}/>
            <input
              style={{ paddingLeft:32, height:36, border:'1px solid var(--border)', borderRadius:8, fontSize:'0.875rem', outline:'none', width:220 }}
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-secondary" onClick={fetchData}>
            <RefreshCw size={15}/> Actualiser
          </button>
        </div>
      </div>

      {/* AOs en cours de sourcing */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header">
          <span className="card-title">
            <Target size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>
            AOs en cours de sourcing ({activeMissions.length})
          </span>
        </div>
        {activeMissions.length === 0 ? (
          <div className="empty-state" style={{ padding:'40px 0' }}>
            <Layers size={32} style={{ marginBottom:12, opacity:0.4 }}/>
            <p style={{ fontSize:'0.875rem', color:'var(--text-muted)' }}>
              {search ? 'Aucun résultat pour cette recherche' : 'Aucune mission en cours de sourcing'}
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Mission</th><th>Client</th><th>Statut</th><th>TJM</th><th>Délai réponse</th><th>Profils scorés</th><th></th></tr>
              </thead>
              <tbody>
                {activeMissions.map(ao => <MissionRow key={ao.id} ao={ao}/>)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* AOs clôturés (shortlist validée et au-delà) */}
      {(closedMissions.length > 0 || search) && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">
              <CheckCircle size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>
              AOs clôturés ({closedMissions.length})
            </span>
          </div>
          {closedMissions.length === 0 ? (
            <div className="empty-state" style={{ padding:'32px 0' }}>
              <p style={{ fontSize:'0.875rem', color:'var(--text-muted)' }}>Aucune mission clôturée</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Mission</th><th>Client</th><th>Statut</th><th>TJM</th><th>Délai réponse</th><th>Profils scorés</th><th></th></tr>
                </thead>
                <tbody>
                  {closedMissions.map(ao => <MissionRow key={ao.id} ao={ao}/>)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DeadlineBadge({ deadline }) {
  const today = new Date();
  const due   = new Date(deadline);
  const diff  = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  if (isNaN(diff)) return <span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{deadline}</span>;
  const color = diff < 0 ? '#DC2626' : diff <= 2 ? '#F59E0B' : diff <= 7 ? '#FF4100' : '#16A34A';
  const bg    = diff < 0 ? 'rgba(239,68,68,0.12)' : diff <= 2 ? '#FFFBEB' : diff <= 7 ? 'var(--orange-50)' : 'rgba(34,197,94,0.08)';
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:bg, color }}>
      <Calendar size={10}/>
      {diff < 0 ? 'Expiré' : diff === 0 ? "Auj." : `${diff}j`}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Portal / ConfirmDeleteModal / ChronoBadge / CorbeilleColumn
// ─────────────────────────────────────────────────────────────
function Portal({ children }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); return () => setMounted(false); }, []);
  if (!mounted) return null;
  const { createPortal } = require('react-dom');
  return createPortal(children, document.body);
}

function ConfirmDeleteModal({ ao, onConfirm, onCancel }) {
  return (
    <Portal>
      <div style={{ position:'fixed', inset:0, zIndex:99998, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onCancel}>
        <div style={{ background:'var(--bg-card)', borderRadius:12, padding:24, maxWidth:400, width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
          <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:16 }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(239,68,68,0.14)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <AlertTriangle size={20} color="#dc2626"/>
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:'1rem', color:'var(--text-primary)', marginBottom:4 }}>Supprimer définitivement ?</div>
              <div style={{ fontSize:'0.875rem', color:'var(--text-muted)', lineHeight:1.5 }}>
                <strong>{ao.job_title || 'Cet AO'}</strong> sera supprimé définitivement. Cette action est <strong>irréversible</strong>.
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button className="btn btn-secondary" onClick={onCancel}>Annuler</button>
            <button
              className="btn btn-danger"
              onClick={onConfirm}
              style={{ background:'#dc2626', borderColor:'#dc2626', color:'white' }}
            >
              <Trash2 size={14}/> Supprimer définitivement
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function ChronoBadge({ ao, onOpenChange }) {
  const [open,    setOpen]    = useState(false);
  const [history, setHistory] = useState(ao._history || []);
  const [loading, setLoading] = useState(false);
  const [pos,     setPos]     = useState({ top:0, left:0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target)) {
        if (!e.target.closest('.kc-popover')) {
          setOpen(false);
          if (onOpenChange) onOpenChange(false);
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const handler = () => {
      setOpen(false);
      if (onOpenChange) onOpenChange(false);
    };
    document.addEventListener('scroll', handler, true);
    return () => document.removeEventListener('scroll', handler, true);
  }, [open, onOpenChange]);

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 8, left: Math.max(8, Math.min(rect.left, window.innerWidth - 240)) });
    }

    if (!open && !history.length) {
      setLoading(true);
      try {
        const res     = await aoAPI.getStatusHistory(ao.ao_id || ao.id);
        setHistory(res.data?.history || []);
      } catch {
      } finally {
        setLoading(false);
      }
    }

    const newOpen = !open;
    setOpen(newOpen);
    if (onOpenChange) onOpenChange(newOpen);
  };

  const duration = ao.current_status_duration;
  const hasTime  = duration != null && duration > 0;
  const getBadgeStyle = () => {
    if (!hasTime) return { background:'rgba(107,114,128,0.1)', color:'var(--text-muted)', border:'1px solid rgba(107,114,128,0.2)' };
    const h = duration / 3600;
    if (h < 24)  return { background:'rgba(37,99,235,0.08)', color:'#FF4100', border:'1px solid rgba(37,99,235,0.2)' };
    if (h < 72)  return { background:'rgba(245,158,11,0.1)', color:'#d97706', border:'1px solid rgba(245,158,11,0.25)' };
    return { background:'rgba(220,38,38,0.08)', color:'#dc2626', border:'1px solid rgba(220,38,38,0.2)' };
  };

  return (
    <>
      <button ref={btnRef} onClick={handleClick} className="kc-chrono-btn" style={getBadgeStyle()} title="Voir l'historique">
        <Clock3 size={11}/>
        <span>{hasTime ? formatDuration(duration) : 'Nouveau'}</span>
        {open ? <ChevronUp size={10}/> : <ChevronDown size={10}/>}
      </button>

      {open && (
        <Portal>
          <div className="kc-popover" style={{ top:pos.top, left:pos.left }} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
            <div className="kc-popover-header">Historique des étapes</div>
            <div className="kc-popover-body">
              {loading && <div className="kc-popover-empty"><Loader size={14} className="kc-spin"/> Chargement…</div>}
              {!loading && history.length === 0 && <div className="kc-popover-empty">Aucun historique</div>}
              {!loading && history.map((h, i) => {
                const isCurrent = h.is_current;
                const cfg = STATUS_CONFIG[(h.status || '').toUpperCase()] || {};
                return (
                  <div key={h.id || i} className={`kc-popover-row ${isCurrent ? 'kc-popover-row--current' : ''}`}>
                    <div
                      className="kc-popover-dot"
                      style={{
                        background: cfg.kanbanColor || 'var(--text-muted)',
                        boxShadow: isCurrent ? `0 0 0 3px ${(cfg.kanbanColor || 'var(--text-muted)')}33` : 'none',
                      }}
                    />
                    <div className="kc-popover-info">
                      <div className="kc-popover-label">
                        {h.label || cfg.label || h.status}
                        {isCurrent && <span className="kc-popover-badge">EN COURS</span>}
                      </div>
                      <div className="kc-popover-duration"><Clock3 size={9}/> {formatDuration(h.duration_seconds)}</div>
                    </div>
                    {i < history.length - 1 && !isCurrent && <ArrowRight size={10} color="#d1d5db"/>}
                  </div>
                );
              })}
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}

function CorbeilleColumn({ items, onAction, isActioning, navigate, user, onRefresh }) {
  const [viding, setViding] = useState(false);
  const sorted = [...items].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const handleViderTout = async () => {
    if (!window.confirm(`Supprimer définitivement les ${items.length} AO de la corbeille ? Cette action est irréversible.`)) return;
    setViding(true);
    let errors = 0;
    for (const ao of items) {
      try {
        await corbeilleAPI.supprimerDefinitivement(ao.ao_id || ao.id);
      } catch {
        errors++;
      }
    }
    if (errors > 0) toast.error(`${errors} AO n'ont pas pu être supprimés`);
    else toast.success('Corbeille vidée');
    setViding(false);
    onRefresh();
  };

  return (
    <div className="kanban-col">
      <div className="kanban-col-header">
        <div className="kanban-col-dot" style={{ background:'var(--text-muted)' }}/>
        <span className="kanban-col-title">🗑 Corbeille</span>
        <span className="kanban-col-count">{items.length}</span>
        {items.length > 0 && (
          <button
            onClick={handleViderTout}
            disabled={viding}
            style={{
              marginLeft:6,
              padding:'2px 6px',
              borderRadius:4,
              border:'1px solid #fca5a5',
              background:'rgba(239,68,68,0.10)',
              color:'#dc2626',
              fontSize:'0.625rem',
              fontWeight:700,
              cursor:'pointer',
              whiteSpace:'nowrap',
            }}
          >
            {viding ? '⏳' : 'Vider tout'}
          </button>
        )}
      </div>

      <div className="kanban-cards">
        {sorted.length === 0 && <div className="kanban-empty">🗑 Corbeille vide</div>}
        {sorted.map(ao => (
          <KanbanCard
            key={ao.ao_id || ao.id}
            ao={ao}
            colKey="SUPPRIME"
            colColor="var(--text-muted)"
            user={user}
            onAction={onAction}
            isActioning={(aoId, a) => isActioning(aoId, a)}
            onOpen={() => navigate(`/ao/${ao.ao_id || ao.id}`)}
            onRefresh={onRefresh}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Kanban Board (Admin / IA)
// ─────────────────────────────────────────────────────────────
function KanbanBoard() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const boardRef  = useRef(null);
  const [board,         setBoard]         = useState({});
 const [filters, setFilters] = useState({ source:'', client:'', location:'', search:'', ia:'', ta:'' });

  const [filterOptions, setFilterOptions] = useState({ sources:[], clients:[], locations:[] });
  const [loading,       setLoading]       = useState(true);
  const [showModal,     setShowModal]     = useState(false);
  const [actioning,     setActioning]     = useState(null);
  const [viewMode,      setViewMode]      = useState('kanban'); // 'kanban' | 'list'

  /*const enrichItems = async (items) => {
    return await Promise.all((items || []).map(async (ao) => {
      try {
        const aoId = ao.ao_id || ao.id;
        if (!aoId) return { ...ao, current_status_duration:0, _history:[] };
        const res     = await aoAPI.getStatusHistory(aoId);
        const history = res.data?.history || [];
        const current = history.find(h => h.is_current);
        return { ...ao, current_status_duration: current?.duration_seconds ?? 0, _history: history };
      } catch {
        return { ...ao, current_status_duration:0, _history:[] };
      }
    }));
  };*/
/*
  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.source)   params.source_type = filters.source;
      if (filters.client)   params.client      = filters.client;
      if (filters.location) params.location    = filters.location;
      if (filters.search)   params.search      = filters.search;

      const [boardRes, filterRes] = await Promise.allSettled([
        kanbanAPI.getBoard(params),
        kanbanAPI.getFilters(),
      ]);

      if (boardRes.status === 'fulfilled') {
        const columns = boardRes.value.data?.columns || [];
        const map = {};
        for (const col of columns) {
          const statusKey     = normalizeStatus(col.status);
          const enrichedItems = await enrichItems(col.items || []);
          map[statusKey]      = enrichedItems;
        }
        setBoard(map);
      }

      if (filterRes.status === 'fulfilled') setFilterOptions(filterRes.value.data || {});
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [filters.source, filters.client, filters.location, filters.search]);
*/


const fetchBoard = useCallback(async () => {
  setLoading(true);
  try {
    const params = {};
    if (filters.source)   params.source_type = filters.source;
    if (filters.client)   params.client      = filters.client;
    if (filters.location) params.location    = filters.location;
    if (filters.search)   params.search      = filters.search;

    const [boardRes, filterRes] = await Promise.allSettled([
      kanbanAPI.getBoard(params),
      kanbanAPI.getFilters(),
    ]);

    if (boardRes.status === 'fulfilled') {
      const columns = boardRes.value.data?.columns || [];

      // ✅ 1. récupérer TOUS les IDs
      const allAos = columns.flatMap(col => col.items || []);
      const aoIds = allAos.map(a => a.ao_id || a.id).filter(Boolean);

      // ✅ 2. appel BATCH UNIQUE
      const historiesRes = await api.post('/ao-management/status-history/batch', aoIds);
      const historiesMap = historiesRes.data || {};

      const map = {};

      for (const col of columns) {
        const statusKey = normalizeStatus(col.status);

        // ✅ 3. enrichissement sans appels API
        const enrichedItems = (col.items || []).map(ao => {
          const aoId = ao.ao_id || ao.id;
          const history = historiesMap[aoId] || [];

          const current = history.find(h => h.is_current);

          return {
            ...ao,
            current_status_duration: current?.duration_seconds ?? 0,
            _history: history
          };
        });

        map[statusKey] = enrichedItems;
      }

      setBoard(map);
    }

    if (filterRes.status === 'fulfilled') {
      setFilterOptions(filterRes.value.data || {});
    }

  } catch (e) {
    toast.error(extractError(e));
  } finally {
    setLoading(false);
  }
}, [filters.source, filters.client, filters.location, filters.search]);


  useEffect(() => { fetchBoard(); }, [fetchBoard]);
/*ajout*/

const iaOptions = React.useMemo(() => {
  const set = new Set();
  Object.values(board).forEach(items =>
    (items || []).forEach(ao => { if (ao.handler_user_name) set.add(ao.handler_user_name); })
  );
  return [...set].sort();
}, [board]);

const taOptions = React.useMemo(() => {
  const set = new Set();
  Object.values(board).forEach(items =>
    (items || []).forEach(ao => { if (ao.talent_acquisition_name) set.add(ao.talent_acquisition_name); })
  );
  return [...set].sort();
}, [board]);

  useEffect(() => {
    const saved = sessionStorage.getItem('kanban_scroll');
    if (saved !== null && boardRef.current) {
      boardRef.current.scrollLeft = Number(saved);
      sessionStorage.removeItem('kanban_scroll');
    }
  }, [loading]);

  const navigateToAO = useCallback((aoId) => {
    if (boardRef.current) {
      sessionStorage.setItem('kanban_scroll', String(boardRef.current.scrollLeft));
    }
    navigate(`/ao/${aoId}`);
  }, [navigate]);

  const [abandonModal, setAbandonModal] = useState(null); // { aoId }
  const [abandonComment, setAbandonComment] = useState('');
  const [abandonSaving, setAbandonSaving] = useState(false);
  const [reponduModal, setReponduModal] = useState(null); // { aoId, aoLabel, topProfils }

  const handleAbandon = async () => {
    if (!abandonComment.trim()) { toast.error('Un commentaire est obligatoire'); return; }
    setAbandonSaving(true);
    try {
      await aoAPI.abandon(abandonModal.aoId, abandonComment.trim());
      toast.success('AO abandonné');
      setAbandonModal(null);
      setAbandonComment('');
      fetchBoard();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setAbandonSaving(false);
    }
  };

  const handleAction = async (aoId, action, aoLabel) => {
    if (action === 'abandonne') { setAbandonModal({ aoId }); return; }
    if (action === 'repondu') {
      try {
        const res = await kanbanAPI.getDetail(aoId);
        const candidates = res.data?.candidates || [];
        const topProfils = candidates.filter(c => c.top_profil || c.top_profil_brouillon);
        setReponduModal({ aoId, aoLabel: aoLabel || 'AO', topProfils });
      } catch (e) {
        toast.error('Impossible de charger les top profils : ' + extractError(e));
      }
      return;
    }
    setActioning(aoId + action);
    try {
      if (action === 'take-charge')   await aoAPI.takeCharge(aoId);
      if (action === 'take-sourcing') await aoAPI.takeSourcing(aoId);
      if (action === 'send-to-ta')    await aoAPI.sendToTA(aoId, null);
      if (action === 'corbeille')     await corbeilleAPI.mettreEnCorbeille(aoId);
      if (action === 'restaurer')     await corbeilleAPI.restaurer(aoId);
      if (action === 'gagne')         await aoAPI.changeStatus(aoId, 'GAGNE');
      if (action === 'perdu')         await aoAPI.changeStatus(aoId, 'PERDU');
      if (action === 'termine')       await aoAPI.changeStatus(aoId, 'TERMINE');

      const msgs = {
        corbeille:'AO mis en corbeille',
        restaurer:'AO restauré',
        'take-charge':'AO pris en charge',
        repondu:'Statut mis à jour',
        gagne:'Statut mis à jour',
        perdu:'Statut mis à jour',
        termine:'Statut mis à jour',
      };
      toast.success(msgs[action] || 'Action effectuée');
      fetchBoard();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setActioning(null);
    }
  };

 /* const filtered = (items) => {
    if (!filters.search) return items;
    const q = filters.search.toLowerCase();
    return items.filter(i =>
      (i.job_title || '').toLowerCase().includes(q) ||
      (i.client || '').toLowerCase().includes(q) ||
      (i.location || '').toLowerCase().includes(q)
    );
  };*/

  const filtered = (items) => {
  let result = items;
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(i =>
      (i.job_title || '').toLowerCase().includes(q) ||
      (i.client || '').toLowerCase().includes(q) ||
      (i.location || '').toLowerCase().includes(q)
    );
  }
  if (filters.ia) result = result.filter(i => i.handler_user_name === filters.ia);
  if (filters.ta) result = result.filter(i => i.talent_acquisition_name === filters.ta);
  return result;
};

  const role         = (user?.role || '').toUpperCase();
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdmin      = role === 'ADMIN' || isSuperAdmin;
  const isIA         = role === 'BUSINESS_MANAGER';

  return (
    <div className="kanban-shell">
      <div className="page-header" style={{ flexShrink:0 }}>
        <div className="page-header-left">
          <h1>Pipeline AO</h1>
          <p>Visualisez et gérez vos appels d'offres par statut</p>
        </div>
        <div className="page-header-actions">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
              title="Vue Kanban"
            >
              <LayoutGrid size={15}/>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vue liste"
            >
              <List size={15}/>
            </button>
          </div>
          <button className="btn btn-secondary" onClick={fetchBoard}><RefreshCw size={15}/></button>
          {isSuperAdmin && (
            <button
              className="btn btn-secondary"
              title="Relancer le traitement IA sur tous les AOs sans titre (FAILED)"
              onClick={async () => {
                try {
                  const r = await aoAPI.reprocessAllFailed();
                  toast.success(r.data?.message || 'Retraitement lancé');
                  setTimeout(fetchBoard, 3000);
                } catch { toast.error('Erreur retraitement'); }
              }}
              style={{ gap: 6, fontSize: '0.8rem' }}
            >
              <RotateCcw size={14}/> Retraiter les échecs
            </button>
          )}
          {(isAdmin || isIA) && (
            <button className="btn btn-orange" onClick={() => setShowModal(true)}>
              <Plus size={16}/> Nouvel AO
            </button>
          )}
        </div>
      </div>

      <div className="filters-bar" style={{ flexShrink:0 }}>
        <div className="filter-search">
          <Search size={15} color="var(--text-muted)"/>
          <input
            placeholder="Rechercher un AO, client, ville…"
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>

<select className="filter-select" value={filters.ia} onChange={e => setFilters(f => ({ ...f, ia: e.target.value }))}>
  <option value="">Tous IA</option>
  {iaOptions.map(name => <option key={name} value={name}>{name}</option>)}
</select>

<select className="filter-select" value={filters.ta} onChange={e => setFilters(f => ({ ...f, ta: e.target.value }))}>
  <option value="">Tous TA</option>
  {taOptions.map(name => <option key={name} value={name}>{name}</option>)}
</select>


        <select className="filter-select" value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))}>
          <option value="">Toutes sources</option>
          {(filterOptions.sources || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select className="filter-select" value={filters.client} onChange={e => setFilters(f => ({ ...f, client: e.target.value }))}>
          <option value="">Tous clients</option>
          {(filterOptions.clients || []).map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select className="filter-select" value={filters.location} onChange={e => setFilters(f => ({ ...f, location: e.target.value }))}>
          <option value="">Toutes villes</option>
          {(filterOptions.locations || []).map(l => <option key={l} value={l}>{l}</option>)}
        </select>

        {(filters.search || filters.source || filters.client || filters.location) && (
          <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ source:'', client:'', location:'', search:'' })}>
            Réinitialiser
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', flex:1, gap:12, color:'var(--text-muted)' }}>
          <div className="spinner"/> Chargement du pipeline…
        </div>
      ) : viewMode === 'list' ? (
        <KanbanListView
          board={board}
          user={user}
          onAction={handleAction}
          actioning={actioning}
          navigate={navigate}
          onRefresh={fetchBoard}
          filtered={filtered}
        />
      ) : (
        <div className="kanban-board" ref={boardRef}>
          {COLUMNS.filter(c => c !== 'SUPPRIME').map(colKey => {
            const cfg   = STATUS_CONFIG[colKey] || { label:colKey, kanbanColor:'var(--text-muted)' };
            const items = filtered(board[colKey] || []);
            return (
              <div key={colKey} className="kanban-col">
                <div className="kanban-col-header">
                  <div className="kanban-col-dot" style={{ background:cfg.kanbanColor }}/>
                  <span className="kanban-col-title">{cfg.label}</span>
                  <span className="kanban-col-count">{items.length}</span>
                </div>

                <div className="kanban-cards">
                  {items.length === 0 && <div className="kanban-empty">Aucun AO</div>}
                  {items.map(ao => (
                    <KanbanCard
                      key={ao.ao_id || ao.id}
                      ao={ao}
                      colKey={colKey}
                      colColor={cfg.kanbanColor}
                      user={user}
                      onAction={handleAction}
                      isActioning={(aoId, a) => actioning === aoId + a}
                      onOpen={() => navigateToAO(ao.ao_id || ao.id)}
                      onRefresh={fetchBoard}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          <CorbeilleColumn
            items={filtered(board['SUPPRIME'] || [])}
            onAction={handleAction}
            isActioning={(aoId, a) => actioning === aoId + a}
            navigate={navigateToAO}
            user={user}
            onRefresh={fetchBoard}
          />
        </div>
      )}

      {showModal && (
        <AOModal
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchBoard(); }}
        />
      )}

      {reponduModal && (
        <KanbanReponduModal
          aoId={reponduModal.aoId}
          aoLabel={reponduModal.aoLabel}
          topProfils={reponduModal.topProfils}
          onClose={() => setReponduModal(null)}
          onConfirm={async (selectedIds) => {
            try {
              await aoAPI.changeStatus(reponduModal.aoId, 'REPONDU', selectedIds);
              const n = selectedIds.length;
              toast.success(`AO marqué Répondu${n > 0 ? ` — ${n} profil${n > 1 ? 's' : ''} positionné${n > 1 ? 's' : ''}` : ''}`);
              setReponduModal(null);
              fetchBoard();
            } catch (e) {
              toast.error(extractError(e));
            }
          }}
        />
      )}

      {abandonModal && (
        <div className="modal-overlay" onClick={() => { setAbandonModal(null); setAbandonComment(''); }}>
          <div className="modal" style={{ maxWidth:480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin:0, fontSize:'1rem', color:'#78350f' }}>Abandonner l'AO</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => { setAbandonModal(null); setAbandonComment(''); }}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom:14, fontSize:'0.875rem', color:'var(--text-secondary)' }}>
                Un commentaire est obligatoire pour expliquer la raison de l'abandon. Il sera affiché sur la fiche AO.
              </p>
              <div className="input-group">
                <label className="input-label">Raison de l'abandon <span style={{ color:'#dc2626' }}>*</span></label>
                <textarea
                  className="input"
                  rows={4}
                  value={abandonComment}
                  onChange={e => setAbandonComment(e.target.value)}
                  placeholder="Ex : Le client a annulé le besoin, budget non validé…"
                  style={{ resize:'vertical' }}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setAbandonModal(null); setAbandonComment(''); }}>Annuler</button>
              <button
                className="btn"
                style={{ background:'#78350f', color:'#fff', opacity: (!abandonComment.trim() || abandonSaving) ? 0.5 : 1 }}
                onClick={handleAbandon}
                disabled={abandonSaving || !abandonComment.trim()}
              >
                {abandonSaving ? <><div className="spinner" style={{ width:14, height:14 }}/> Abandon…</> : "Confirmer l'abandon"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// KanbanListView — vue tableau de tous les AOs
// ─────────────────────────────────────────────────────────────
function KanbanListView({ board, user, onAction, actioning, navigate, onRefresh, filtered }) {
  const role      = (user?.role || '').toUpperCase();
  const isAdmin   = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const isIA      = role === 'BUSINESS_MANAGER';
  const [activeStatus, setActiveStatus] = useState(null);

  const allAOs = React.useMemo(() => {
    const flat = [];
    for (const colKey of COLUMNS) {
      for (const ao of (board[colKey] || [])) {
        flat.push({ ...ao, _colKey: colKey });
      }
    }
    return flat;
  }, [board]);

  const totalByStatus = React.useMemo(() => {
    const counts = {};
    for (const ao of allAOs) {
      counts[ao._colKey] = (counts[ao._colKey] || 0) + 1;
    }
    return counts;
  }, [allAOs]);

  const baseFiltered = filtered ? filtered(allAOs) : allAOs;
  const displayed    = activeStatus ? baseFiltered.filter(ao => ao._colKey === activeStatus) : baseFiltered;

  const toggleStatus = (colKey) => setActiveStatus(prev => prev === colKey ? null : colKey);

  return (
    <div className="ao-list-shell">
      {/* Résumé par statut — cliquables pour filtrer */}
      <div className="ao-list-summary">
        {COLUMNS.filter(k => (totalByStatus[k] || 0) > 0).map(colKey => {
          const cfg      = STATUS_CONFIG[colKey] || { label: colKey, kanbanColor: 'var(--text-muted)' };
          const isActive = activeStatus === colKey;
          return (
            <button
              key={colKey}
              className={`ao-list-summary-chip ${isActive ? 'ao-list-summary-chip--active' : ''}`}
              style={{
                borderColor:  isActive ? cfg.kanbanColor : cfg.kanbanColor + '60',
                background:   isActive ? cfg.kanbanColor + '28' : cfg.kanbanColor + '12',
                boxShadow:    isActive ? `0 0 0 2px ${cfg.kanbanColor}40` : 'none',
              }}
              onClick={() => toggleStatus(colKey)}
            >
              <span className="ao-list-summary-dot" style={{ background: cfg.kanbanColor }}/>
              <span style={{ color: cfg.kanbanColor, fontWeight: 700 }}>{totalByStatus[colKey]}</span>
              <span style={{ color: isActive ? cfg.kanbanColor : 'var(--text-muted)', fontSize: '0.7rem', fontWeight: isActive ? 600 : 400 }}>{cfg.label}</span>
              {isActive && <X size={11} style={{ color: cfg.kanbanColor, marginLeft: 2 }}/>}
            </button>
          );
        })}
        {activeStatus && (
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem', padding: '2px 8px' }} onClick={() => setActiveStatus(null)}>
            Tout voir
          </button>
        )}
      </div>

      {/* Tableau */}
      <div className="ao-list-table-wrap">
        <table className="ao-list-table">
          <thead>
            <tr>
              <th>Statut</th>
              <th>Mission</th>
              <th>Client</th>
              <th>Ville</th>
              <th>Source</th>
              <th>IA</th>
              <th>TA</th>
              <th style={{ textAlign: 'center' }}>Durée</th>
              <th style={{ textAlign: 'center' }}>Pipeline</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  Aucun AO trouvé
                </td>
              </tr>
            )}
            {displayed.map(ao => {
              const aoId     = ao.ao_id || ao.id;
              const colKey   = ao._colKey;
              const cfg      = STATUS_CONFIG[colKey] || { label: colKey, kanbanColor: 'var(--text-muted)' };
              const hideStats = shouldHidePipelineStats(colKey);
              const isCorbeille = colKey === 'SUPPRIME';

              const canTakeCharge = colKey === 'NOUVEAU' && (isAdmin || isIA);
              const canCorbeille  = !isCorbeille && (isAdmin || isIA);
              const canRestaurer  = isCorbeille && (isAdmin || isIA);

              return (
                <tr key={aoId} className="ao-list-row" onClick={() => navigate(`/ao/${aoId}`)}>
                  <td>
                    <span className="ao-list-badge" style={{ color: cfg.kanbanColor, background: cfg.kanbanColor + '18', border: `1px solid ${cfg.kanbanColor}40` }}>
                      <span className="ao-list-badge-dot" style={{ background: cfg.kanbanColor }}/>
                      {cfg.label}
                    </span>
                  </td>
                  <td className="ao-list-title-cell">
                    <span className="ao-list-title">{ao.job_title || '—'}</span>
                    {ao.source_type && <span className="ao-list-source-inline">{ao.source_type}</span>}
                    {ao.ao_code && (
                      ao.fiche_poste_url
                        ? <a href={ao.fiche_poste_url} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ marginLeft:6, fontSize:'0.65rem', fontWeight:700, color:'#FF4100', textDecoration:'none' }}>
                            #{ao.ao_code}
                          </a>
                        : <span style={{ marginLeft:6, fontSize:'0.65rem', fontWeight:700, color:'var(--text-muted)' }}>#{ao.ao_code}</span>
                    )}
                  </td>
                  <td>
                    {ao.client
                      ? <span className="ao-list-meta"><Building2 size={12}/> {ao.client}</span>
                      : <span className="ao-list-empty">—</span>}
                  </td>
                  <td>
                    {ao.location
                      ? <span className="ao-list-meta"><MapPin size={12}/> {ao.location}</span>
                      : <span className="ao-list-empty">—</span>}
                  </td>
                  <td>
                    {ao.source_type
                      ? <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>{ao.source_type}</span>
                      : <span className="ao-list-empty">—</span>}
                  </td>
                  <td>
                    {ao.handler_user_name
                      ? <div className="ao-list-assignee"><div className="avatar avatar-sm">{initials(ao.handler_user_name)}</div><span>{ao.handler_user_name}</span></div>
                      : <span className="ao-list-empty">—</span>}
                  </td>
                  <td>
                    {ao.talent_acquisition_name
                      ? <div className="ao-list-assignee"><div className="avatar avatar-sm" style={{ background:'var(--orange-100)', color:'var(--orange-600)' }}>{initials(ao.talent_acquisition_name)}</div><span>{ao.talent_acquisition_name}</span></div>
                      : <span className="ao-list-empty">—</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {ao.current_status_duration > 0
                      ? <span className="ao-list-duration"><Clock3 size={11}/> {formatDuration(ao.current_status_duration)}</span>
                      : <span className="ao-list-empty">—</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {!hideStats ? (
                      <div className="ao-list-stats">
                        <span className="ao-list-stat" title="Profils scorés"><Users size={11}/> {ao.scored_candidates_count ?? 0}</span>
                        <span className="ao-list-stat ao-list-stat--orange" title="Shortlist"><Award size={11}/> {ao.shortlist_count ?? 0}</span>
                        <span className="ao-list-stat ao-list-stat--gold" title="Top profils"><Star size={11}/> {ao.top_profil_count ?? 0}</span>
                      </div>
                    ) : <span className="ao-list-empty">—</span>}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="ao-list-actions">
                      {canTakeCharge && (
                        <button
                          className="btn btn-primary btn-sm kc-action-btn"
                          style={{ fontSize: '0.7rem', padding: '3px 10px', whiteSpace: 'nowrap' }}
                          disabled={actioning === aoId + 'take-charge'}
                          onClick={() => onAction(aoId, 'take-charge')}
                        >
                          {actioning === aoId + 'take-charge' ? <Loader size={11}/> : <UserCheck size={11}/>} Prendre
                        </button>
                      )}
                      {canRestaurer && (
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ fontSize: '0.7rem', padding: '3px 8px', color: '#059669', borderColor: '#86efac' }}
                          disabled={actioning === aoId + 'restaurer'}
                          onClick={() => onAction(aoId, 'restaurer')}
                        >
                          {actioning === aoId + 'restaurer' ? <Loader size={11}/> : <Restore size={11}/>}
                        </button>
                      )}
                      {canCorbeille && (
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title="Mettre en corbeille"
                          disabled={actioning === aoId + 'corbeille'}
                          onClick={() => onAction(aoId, 'corbeille')}
                          style={{ color: '#9ca3af' }}
                        >
                          {actioning === aoId + 'corbeille' ? <Loader size={12}/> : <Trash2 size={13}/>}
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        title="Ouvrir"
                        onClick={() => navigate(`/ao/${aoId}`)}
                        style={{ color: 'var(--text-muted)' }}
                      >
                        <ChevronRight size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// KanbanCard
// ─────────────────────────────────────────────────────────────
function KanbanCard({ ao, colKey, colColor, user, onAction, isActioning, onOpen, onRefresh }) {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [popoverOpen,    setPopoverOpen]    = useState(false);
  const [confirmDelete,  setConfirmDelete]  = useState(false);
  const [deleting,       setDeleting]       = useState(false);
  const [showAssignIA,   setShowAssignIA]   = useState(false);
  const [showSendTA,     setShowSendTA]     = useState(false);
  const [gagneModal,     setGagneModal]     = useState(false);

  const role         = (user?.role || '').toUpperCase();
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdmin      = role === 'ADMIN' || isSuperAdmin;
  const isIA         = role === 'BUSINESS_MANAGER';
  const isTA         = role === 'TALENT_ACQUISITION';

  const isCorbeille = colKey === 'SUPPRIME';
  const hideStats   = shouldHidePipelineStats(colKey || ao.ao_avancement);

  const canTakeCharge   = colKey === 'NOUVEAU' && (isAdmin || isIA);
  const canSendToTA     = colKey === 'EN_COURS_DE_TRAITEMENT' && (isAdmin || isIA);
  const canTakeSourcing =
    (colKey === 'ENVOYE_A_EQUIPE_TALENT_ACQUISITION' && (isAdmin || isIA || isTA)) ||
    (colKey === 'EN_COURS_DE_TRAITEMENT' && (isAdmin || isIA));
  const canCorbeille    = !isCorbeille && (isAdmin || isIA);
  const canChangeStatus = (isAdmin || isIA) && ['TOP_PROFIL_ENVOYE_AU_CLIENT','REPONDU','GAGNE','PERDU','SHORT_LIST_ENVOYEE'].includes(colKey);
  const canAbandon      = (isAdmin || isIA) && !isCorbeille && !['NOUVEAU','SUPPRIME','ABANDONNE','TERMINE','GAGNE'].includes(colKey);

  const nextStatuses = {
    SHORT_LIST_ENVOYEE:          [{ key:'repondu', label:'Répondu',  icon:<CheckCircle size={12}/> }],
    TOP_PROFIL_ENVOYE_AU_CLIENT: [{ key:'repondu', label:'Répondu',  icon:<CheckCircle size={12}/> }],
    REPONDU: [
      { key:'gagne', label:'Gagné 🏆', icon:<Trophy size={12}/> },
      { key:'perdu', label:'Perdu',    icon:<XCircle size={12}/> },
    ],
    GAGNE: [{ key:'termine', label:'Clôturer', icon:<RotateCcw size={12}/> }],
    PERDU: [{ key:'termine', label:'Clôturer', icon:<RotateCcw size={12}/> }],
  };

  const aoId = ao.ao_id || ao.id;

  const handleSupprimerDefinitivement = async () => {
    setDeleting(true);
    try {
      await corbeilleAPI.supprimerDefinitivement(aoId);
      toast.success('AO supprimé définitivement');
      setConfirmDelete(false);
      onRefresh();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className={`kanban-card ${popoverOpen ? 'kanban-card--popover-open' : ''} ${isCorbeille ? 'kanban-card--corbeille' : ''}`} onClick={onOpen}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6 }}>
          <div className="kc-title">{ao.job_title || 'Sans titre'}</div>
          <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
            {!isCorbeille && <ChronoBadge ao={ao} onOpenChange={setPopoverOpen}/>}
            <button className="btn btn-ghost btn-icon btn-sm kc-open" onClick={e => { e.stopPropagation(); onOpen(); }}>
              <ChevronRight size={14}/>
            </button>
          </div>
        </div>

        {isCorbeille && ao.statut_avant_corbeille && (
          <div
            style={{
              display:'inline-flex',
              alignItems:'center',
              gap:5,
              padding:'3px 8px',
              borderRadius:20,
              background:'rgba(107,114,128,0.1)',
              border:'1px solid rgba(107,114,128,0.2)',
              fontSize:'0.6875rem',
              color:'var(--text-muted)',
              fontWeight:600,
            }}
          >
            <RotateCcw size={10}/>
            Était : {STATUS_CONFIG[ao.statut_avant_corbeille]?.label || ao.statut_avant_corbeille}
          </div>
        )}

        {ao.status === 'FAILED' && (
          <div style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 8px', borderRadius:20,
            background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.25)',
            fontSize:'0.65rem', color:'#DC2626', fontWeight:700, marginBottom:4 }}>
            <AlertTriangle size={10}/> Traitement IA échoué
          </div>
        )}

        <div className="kc-meta">
          {ao.client      && <div className="kc-meta-row"><Building2 size={12}/><span>{ao.client}</span></div>}
          {ao.location    && <div className="kc-meta-row"><MapPin size={12}/><span>{ao.location}</span></div>}
          {ao.ao_code && (
            ao.fiche_poste_url
              ? <div className="kc-meta-row" style={{ gap:4 }}>
                  <a href={ao.fiche_poste_url} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ fontSize:'0.625rem', fontWeight:700, color:'#FF4100', textDecoration:'none' }}>
                    #{ao.ao_code}
                  </a>
                </div>
              : <div className="kc-meta-row"><span style={{ fontSize:'0.625rem', fontWeight:700, color:'var(--text-muted)' }}>#{ao.ao_code}</span></div>
          )}
          {ao.source_type && <span className="badge badge-gray" style={{ fontSize:'0.625rem' }}>{ao.source_type}</span>}
        </div>

        {!hideStats && (
          <div className="kc-stats">
            <div className="kc-stat" title="Profils scorés"><Users size={11}/> {ao.scored_candidates_count ?? 0}</div>
            <div className="kc-stat kc-stat-orange" title="Shortlist"><Award size={11}/> {ao.shortlist_count ?? 0}</div>
            <div className="kc-stat kc-stat-gold" title="Top profils"><Star size={11}/> {ao.top_profil_count ?? 0}</div>
          </div>
        )}

        {(ao.handler_user_name || ao.talent_acquisition_name) && (
          <div className="kc-assignees">
            {ao.handler_user_name && (
              <div className="kc-assignee">
                <div className="avatar avatar-sm">{initials(ao.handler_user_name)}</div>
                <span>{ao.handler_user_name}</span>
              </div>
            )}
            {ao.talent_acquisition_name && (
              <div className="kc-assignee">
                <div className="avatar avatar-sm" style={{ background:'var(--orange-100)', color:'var(--orange-600)' }}>
                  {initials(ao.talent_acquisition_name)}
                </div>
                <span>{ao.talent_acquisition_name}</span>
              </div>
            )}
          </div>
        )}

        <div className="kc-actions" onClick={e => e.stopPropagation()}>
          {isCorbeille && (
            <>
              <button
                className="btn btn-secondary btn-sm kc-action-btn"
                disabled={isActioning(aoId, 'restaurer')}
                onClick={() => onAction(aoId, 'restaurer')}
                style={{ color:'#059669', borderColor:'#86efac' }}
              >
                {isActioning(aoId, 'restaurer') ? <Loader size={12}/> : <Restore size={12}/>} Restaurer
              </button>
              <button
                className="btn btn-danger btn-sm kc-action-btn"
                disabled={deleting}
                onClick={() => setConfirmDelete(true)}
                style={{ background:'#dc2626', borderColor:'#dc2626', color:'white' }}
              >
                {deleting ? <Loader size={12}/> : <Trash2 size={12}/>} Supprimer
              </button>
            </>
          )}

          {!isCorbeille && (
            <>
              {canTakeCharge && (
                <button
                  className="btn btn-primary btn-sm kc-action-btn"
                  disabled={isActioning(aoId, 'take-charge')}
                  onClick={() => onAction(aoId, 'take-charge')}
                >
                  {isActioning(aoId, 'take-charge') ? <Loader size={12}/> : <UserCheck size={12}/>} Prendre en charge
                </button>
              )}

              {canTakeCharge && isAdmin && (
                <button
                  className="btn btn-secondary btn-sm kc-action-btn"
                  onClick={() => setShowAssignIA(true)}
                >
                  <Users size={12}/> Affecter à un IA
                </button>
              )}

              {canTakeSourcing && (
                <button
                  className="btn btn-secondary btn-sm kc-action-btn"
                  disabled={isActioning(aoId, 'take-sourcing')}
                  onClick={() => onAction(aoId, 'take-sourcing')}
                >
                  {isActioning(aoId, 'take-sourcing') ? <Loader size={12}/> : null} Sourcing
                </button>
              )}

              {canSendToTA && (
                <button
                  className="btn btn-secondary btn-sm kc-action-btn"
                  onClick={() => setShowSendTA(true)}
                >
                  <UserCheck size={12}/> Affecter à un TA
                </button>
              )}

              {canChangeStatus && nextStatuses[colKey] && (
                <div style={{ position:'relative' }}>
                  <button className="btn btn-secondary btn-sm kc-action-btn" onClick={e => { e.stopPropagation(); setShowStatusMenu(s => !s); }}>
                    Avancer <ChevronDown size={11}/>
                  </button>

                  {showStatusMenu && (
                    <div
                      style={{
                        position:'absolute',
                        bottom:'100%',
                        left:0,
                        zIndex:100,
                        background:'var(--surface)',
                        border:'1px solid var(--border)',
                        borderRadius:'var(--radius-md)',
                        boxShadow:'var(--shadow-lg)',
                        minWidth:140,
                        padding:'4px 0',
                        marginBottom:4,
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      {nextStatuses[colKey].map(ns => (
                        <button
                          key={ns.key}
                          className="btn btn-ghost btn-sm"
                          style={{ width:'100%', justifyContent:'flex-start', gap:6, borderRadius:0, padding:'6px 12px' }}
                          disabled={isActioning(aoId, ns.key)}
                          onClick={() => {
                            setShowStatusMenu(false);
                            if (ns.key === 'gagne') { setGagneModal(true); }
                            else { onAction(aoId, ns.key, ao.job_title || ''); }
                          }}
                        >
                          {ns.icon} {ns.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {canAbandon && (
                <button
                  className="btn btn-ghost btn-icon btn-sm"
                  title="Abandonner l'AO"
                  onClick={() => onAction(aoId, 'abandonne')}
                  style={{ color:'#78350f' }}
                >
                  <XCircle size={12}/>
                </button>
              )}

              {canCorbeille && (
                <button
                  className="btn btn-ghost btn-icon btn-sm"
                  title="Mettre en corbeille"
                  disabled={isActioning(aoId, 'corbeille')}
                  onClick={() => onAction(aoId, 'corbeille')}
                  style={{ color:'var(--text-muted)' }}
                >
                  {isActioning(aoId, 'corbeille') ? <Loader size={12}/> : <Trash2 size={12}/>}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDeleteModal ao={ao} onConfirm={handleSupprimerDefinitivement} onCancel={() => setConfirmDelete(false)}/>
      )}

      {showAssignIA && (
        <AssignIAModal
          ao={ao}
          onClose={() => setShowAssignIA(false)}
          onSaved={() => { setShowAssignIA(false); onRefresh(); }}
        />
      )}

      {showSendTA && (
        <SendToTAModal
          ao={ao}
          onClose={() => setShowSendTA(false)}
          onSaved={() => { setShowSendTA(false); onRefresh(); }}
        />
      )}

      {gagneModal && (
        <GagnePositionnementModal
          ao={ao}
          onClose={() => setGagneModal(false)}
          onDone={() => { setGagneModal(false); onRefresh(); }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// GagnePositionnementModal
// ─────────────────────────────────────────────────────────────
function GagnePositionnementModal({ ao, onClose, onDone }) {
  const aoId = ao.ao_id || ao.id;
  const client = ao.client || '';
  const label  = ao.job_title || 'Mission';

  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [topProfils,      setTopProfils]      = useState([]);
  const [selected,        setSelected]        = useState(new Set());
  const [missionStartDate, setMissionStartDate] = useState('');
  const [saving,          setSaving]          = useState(false);
  const [log,             setLog]             = useState([]);
  const [done,            setDone]            = useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await kanbanAPI.getDetail(aoId);
        const cands = (res.data?.candidates || []).filter(c => c.top_profil || c.top_profil_brouillon);
        setTopProfils(cands);
        setSelected(new Set(cands.map(c => c.candidate_id)));
      } catch { setTopProfils([]); }
      finally { setLoadingProfiles(false); }
    })();
  }, [aoId]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const addLog = (msg) => setLog(prev => [...prev, msg]);

  const handleConfirm = async () => {
    setSaving(true);
    const errors = [];
    let successCount = 0;

    // 1. Changer statut AO → GAGNE
    try {
      await aoAPI.changeStatus(aoId, 'GAGNE');
      addLog('✅ AO marqué Gagné');
    } catch (e) {
      toast.error('Erreur statut : ' + extractError(e));
      setSaving(false);
      return;
    }

    // 2. Traiter chaque profil sélectionné
    for (const candidateId of [...selected]) {
      const profile = topProfils.find(p => String(p.candidate_id) === String(candidateId));
      if (!profile) continue;

      try {
        // Charger les détails du candidat
        let cand = { ...profile };
        try {
          const det = await candidateAPI.detail(candidateId);
          const d = det.data;
          cand = { ...cand, email: d.email, phone: d.phone, linkedin_link: d.linkedin_link,
                   cv_minio_path: d.cv_minio_path, cv_link: d.cv_link };
        } catch {}

        addLog(`⏳ ${cand.full_name}…`);

        // Déduplication : chercher ressource existante
        let resourceId = null;
        if (cand.email) {
          try {
            const sr = await resourcesAPI.list({ search: cand.email });
            const ex = (sr.data || []).find(r => r.email === cand.email);
            if (ex) resourceId = ex.id;
          } catch {}
        }
        if (!resourceId && cand.full_name) {
          try {
            const sr = await resourcesAPI.list({ search: cand.full_name });
            const ex = (sr.data || []).find(r => r.full_name === cand.full_name);
            if (ex) resourceId = ex.id;
          } catch {}
        }

        if (resourceId) {
          await resourcesAPI.update(resourceId, {
            current_client: client, current_mission: label,
            mission_start_date: missionStartDate || null, status: 'ACTIF',
          });
          addLog('  🔄 Ressource existante mise à jour');
        } else {
          const rRes = await resourcesAPI.create({
            full_name: cand.full_name || 'Inconnu', title: cand.title || '',
            email: cand.email || null, phone: cand.phone || null,
            linkedin_link: cand.linkedin_link || null, location: cand.location || null,
            availability: missionStartDate || null, current_client: client,
            current_mission: label, mission_start_date: missionStartDate || null,
            employee_type: 'INTERCO', status: 'ACTIF',
          });
          resourceId = rRes.data?.id;
          addLog('  ➕ Nouvelle ressource créée');
        }

        // Ajouter une mission (crée aussi le besoin CRM si client existe)
        if (resourceId) {
          try {
            await resourcesAPI.createMission(resourceId, {
              resource_id: resourceId, mission_title: label,
              client_name: client || null,
              start_date: missionStartDate || null,
              status: 'EN_COURS', location: ao.location || null,
            });
            addLog('  📋 Mission ajoutée + besoin client créé');
          } catch {}
        }

        // CV original
        if (resourceId) {
          try {
            let cvUrl = null;
            if (cand.cv_minio_path) {
              const r = await candidateAPI.getCvUrl(candidateId);
              cvUrl = r.data?.cv_url;
            } else if (cand.cv_link?.startsWith('http')) {
              cvUrl = cand.cv_link;
            }
            if (cvUrl) {
              await resourcesAPI.createDocument(resourceId, {
                resource_id: resourceId,
                title: `CV Original — ${cand.full_name}`,
                document_type: 'CV', file_url: cvUrl,
              });
              addLog('  📎 CV original attaché');
            }
          } catch {}
        }

        // CV Wikeys (chercher par candidate_id dans la liste des CVs convertis)
        if (resourceId) {
          try {
            const cvList = await cvAPI.list();
            const cvWikeys = (cvList.data || []).find(
              cv => String(cv.candidate_id) === String(candidateId)
            );
            if (cvWikeys) {
              let wikeysUrl = cvWikeys.url;
              if (!wikeysUrl) {
                const regen = await cvAPI.regenerateDocx(cvWikeys.id);
                wikeysUrl = regen.data?.url;
              }
              if (wikeysUrl) {
                await resourcesAPI.createDocument(resourceId, {
                  resource_id: resourceId,
                  title: `CV Wikeys — ${cand.full_name}`,
                  document_type: 'CV', file_url: wikeysUrl,
                });
                addLog('  📄 CV Wikeys attaché');
              }
            }
          } catch {}
        }

        // Positionnement CRM client
        if (client) {
          try {
            const compRes = await companiesAPI.list({ search: client });
            const company = (compRes.data || []).find(c => c.name === client);
            if (company) {
              await candidatePositioningAPI.create({
                candidate_id: candidateId, target_type: 'CLIENT',
                target_id: company.id, target_label: client, status: 'POSITIONNE',
              });
              addLog('  🏢 Positionnement client CRM enregistré');
            }
          } catch {}
        }

        successCount++;
        addLog(`✅ ${cand.full_name} — traitement terminé`);
      } catch (err) {
        errors.push(`${profile.full_name} : ${extractError(err)}`);
        addLog(`❌ Erreur pour ${profile.full_name}`);
      }
    }

    errors.forEach(m => toast.error(m, { duration: 6000 }));
    toast.success(
      successCount > 0
        ? `AO Gagné — ${successCount} ressource${successCount > 1 ? 's' : ''} positionnée${successCount > 1 ? 's' : ''}`
        : 'AO marqué Gagné'
    );
    setSaving(false);
    setDone(true);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div className="modal" style={{ maxWidth: 600, width: '100%' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ borderBottom: '3px solid #15803d' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Trophy size={20} color="#15803d" />
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Marquer l'AO comme Gagné</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{label}{client ? ` — ${client}` : ''}</div>
            </div>
          </div>
          {!saving && <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={16} /></button>}
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '62vh', overflowY: 'auto' }}>
          {done ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {log.map((l, i) => (
                <div key={i} style={{ fontSize: '0.8125rem', fontFamily: 'monospace',
                  color: l.startsWith('❌') ? '#dc2626' : l.startsWith('✅') ? '#16a34a' : 'var(--text-primary)' }}>
                  {l}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 8 }}>
                <div style={{ fontSize: '0.8125rem', color: '#4ADE80', fontWeight: 600, marginBottom: 4 }}>
                  Sélectionnez les top profils à positionner chez le client
                </div>
                <div style={{ fontSize: '0.75rem', color: '#4d7c0f' }}>
                  Les profils sélectionnés deviendront des ressources avec CV original + CV Wikeys, une mission et un positionnement CRM. L'AO apparaîtra en tant que besoin dans la fiche client.
                </div>
              </div>

              {loadingProfiles ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div className="spinner" style={{ display: 'inline-block', width: 20, height: 20 }} />
                </div>
              ) : topProfils.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                  Aucun top profil défini — l'AO sera quand même marqué Gagné.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {topProfils.map(c => (
                    <label key={c.candidate_id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${selected.has(c.candidate_id) ? 'rgba(34,197,94,0.35)' : 'var(--border)'}`,
                      background: selected.has(c.candidate_id) ? 'rgba(34,197,94,0.08)' : 'var(--bg-elevated)',
                    }}>
                      <input type="checkbox" checked={selected.has(c.candidate_id)}
                        onChange={() => toggle(c.candidate_id)}
                        style={{ width: 16, height: 16, accentColor: '#4ADE80' }} />
                      <div className="avatar avatar-sm" style={{ border: '2px solid #FCD34D', flexShrink: 0 }}>
                        {initials(c.full_name || '??')}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.full_name}</div>
                        {c.title && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>}
                      </div>
                      {c.score_overall != null && (
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#4ADE80', background: 'rgba(34,197,94,0.14)', padding: '2px 8px', borderRadius: 20 }}>
                          {c.score_overall}/100
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: 5 }}>
                  Date de démarrage de mission (optionnel)
                </label>
                <input type="date" className="input" value={missionStartDate}
                  onChange={e => setMissionStartDate(e.target.value)} style={{ width: '100%' }} />
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {done ? (
            <button className="btn btn-primary" onClick={onDone} style={{ background: '#4ADE80', borderColor: '#4ADE80' }}>
              <CheckCircle size={14} /> Fermer
            </button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
              <button className="btn btn-primary" onClick={handleConfirm}
                disabled={saving || loadingProfiles}
                style={{ background: '#4ADE80', borderColor: '#4ADE80', opacity: saving ? 0.75 : 1 }}>
                {saving
                  ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Traitement…</>
                  : <><Trophy size={14} /> Confirmer — Gagné{selected.size > 0 ? ` (${selected.size} profil${selected.size > 1 ? 's' : ''})` : ''}</>
                }
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Modal Kanban : Marquer AO Répondu ────────────────────────
function KanbanReponduModal({ aoId, aoLabel, topProfils, onClose, onConfirm }) {
  const [selected, setSelected] = React.useState(() => new Set(topProfils.map(c => c.candidate_id)));
  const [saving, setSaving] = React.useState(false);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm([...selected]); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '3px solid #16a34a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle size={20} color="#16a34a" />
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Marquer l'AO comme Répondu</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{aoLabel}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ padding: '10px 14px', background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 8, fontSize: '0.8125rem', color: '#16a34a', fontWeight: 600 }}>
            Sélectionnez les top profils à créer en positionnements
          </div>

          {topProfils.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              Aucun top profil — l'AO sera quand même marqué Répondu.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {topProfils.map(c => (
                <label key={c.candidate_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, border: `1px solid ${selected.has(c.candidate_id) ? 'rgba(22,163,74,0.35)' : 'var(--border)'}`, background: selected.has(c.candidate_id) ? 'rgba(22,163,74,0.08)' : 'var(--bg-elevated)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selected.has(c.candidate_id)} onChange={() => toggle(c.candidate_id)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#16a34a' }} />
                  <div className="avatar avatar-sm" style={{ border: '2px solid #FCD34D', flexShrink: 0 }}>{initials(c.full_name || '??')}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.full_name}</div>
                    {c.title && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>}
                  </div>
                  {c.score_overall != null && (
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', background: 'rgba(22,163,74,0.14)', padding: '2px 7px', borderRadius: 20 }}>
                      {c.score_overall}/100
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={saving}
            style={{ background: '#16a34a', borderColor: '#16a34a', opacity: saving ? 0.7 : 1 }}>
            {saving ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Traitement…</> : <><CheckCircle size={14} /> Confirmer{selected.size > 0 ? ` — ${selected.size} positionné${selected.size > 1 ? 's' : ''}` : ''}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
